import {Injectable}                                 from '@angular/core';
import {ApDynformsConfigFieldset}                   from '../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {ApDynformsConfigComboBox}                   from '../../../ap-dynforms/config/ap-dynforms-config-combobox';
import {delay, filter, map}                         from 'rxjs/operators';
import {DriverStore}                                from '../../../stores/docu/driver.store';
import {MachineStore}                               from '../../../stores/docu/machine.store';
import {InstrumentStore}                            from '../../../stores/docu/instrument.store';
import {ApDynformsValidator}                        from '../../../ap-dynforms/ap-dynforms-validator';
import {Validators}                                 from '@angular/forms';
import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import {TranslationStore}                           from '../../../stores/translation/translation.store';
import {FertilizerStore}                            from '../../../stores/base-data/fertilizer.store';
import {FertilizerInorganicStore}                   from '../../../stores/base-data/fertilizer.inorganic.store';
import {FertilizerOrganicStore}                     from '../../../stores/base-data/fertilizer.organic.store';
import {CombinedFertilizer}                         from '../../../types/common-types';
import IDriver = Data.DocuContext.Driver.IDriver;
import IMachine = Data.DocuContext.Machine.IMachine;
import IInstrument = Data.DocuContext.Instrument.IInstrument;

@Injectable({
  providedIn: 'root'
})
export class GeneralConfig {
  constructor(private driverStore: DriverStore,
              private fertilizerInorganicStore: FertilizerInorganicStore,
              private fertilizerOrganicStore: FertilizerOrganicStore,
              private fertilizerStore: FertilizerStore,
              private instrumentStore: InstrumentStore,
              private machineStore: MachineStore,
              private translationStore: TranslationStore) {
  }

  public getDocuFieldSet(formValues: { Driver?: IDriver, Machine?: IMachine, Instrument?: IInstrument },
                         formValues$: Observable<any>, currentFormValues: () => any, isModalField: boolean): ApDynformsConfigFieldset {
    return new ApDynformsConfigFieldset({
      key: 'Docu',
      legend: 'Global__Documentation',
      useMaxWidth: isModalField,
      config: [
        new ApDynformsConfigComboBox({
          key: 'Machine',
          label: 'Settings__Lbl_Menu_Devices',
          textField: 'NameWithSign',
          valueField: 'Id',
          valuePrimitive: false,
          value: formValues.Machine ?? null,
          options: this.machineStore.Listen(s => s.data),
          sort: 1
        }),
        new ApDynformsConfigComboBox({
          key: 'Instrument',
          label: 'Docu_Ini__Tool',
          textField: 'Name',
          valueField: 'Id',
          valuePrimitive: false,
          value: formValues.Instrument ?? null,
          value$: formValues$.pipe(
            filter(values => values['Machine'] || values['Instrument']),
            map(values => {
              if (values['Instrument']) {
                return values['Instrument'];
              }
              if (currentFormValues['Instrument']) {
                return currentFormValues['Instrument'];
              }
              if (values['Machine'].DefaultInstrument) {
                return values['Machine'].DefaultInstrument;
              }
              return null;
            })
          ),
          options: this.instrumentStore.Listen(s => s.data),
          sort: 1
        }),
        new ApDynformsConfigComboBox({
          key: 'Driver',
          label: 'Docu_Ini__Motorist',
          textField: 'ShortFullName',
          valueField: 'Id',
          valuePrimitive: false,
          value: formValues.Driver ?? null,
          value$: formValues$.pipe(
            filter(values => values['Machine'] || values['Driver']),
            map(values => {
              if (values['Driver']) {
                return values['Driver'];
              }
              if (currentFormValues['Driver']) {
                return currentFormValues['Driver'];
              }
              if (values['Machine'].DefaultDriver) {
                return values['Machine'].DefaultDriver;
              }
              return null;
            })
          ),
          options: this.driverStore.Listen(s => s.data),
          sort: 1
        })
      ]
    });
  }

  public getProductFieldSet(formValues: { Group?: number, Product?: number },
                            formValues$: BehaviorSubject<any>): ApDynformsConfigFieldset {
    const fertilizerOptions = combineLatest([
      formValues$,
      this.fertilizerStore.OrganicFertilizer$,
      this.fertilizerStore.InorganicFertilizer$
    ]).pipe(map(([values, organic, inorganic]) => {
      let fert: CombinedFertilizer[] = [];
      switch (values['Group']) {
        case -1:
          return [{
            Id: 0,
            Shortname: this.translationStore.FindTranslationForSelectedLanguage('Nutrients_Pages__PureNutrient')
          }];
        case 0:
          fert = inorganic.filter((i) => i.ContentN > 0);
          break;
        case 1:
          fert = organic.filter((o) => o.ContentN > 0);
          break;
      }
      return fert.sort((first, second) => {
        if (first.Prodname == null) {
          return 1;
        }
        if (second.Prodname == null) {
          return -1;
        }
        return first.Prodname.localeCompare(second.Prodname);
      });
    }));
    return new ApDynformsConfigFieldset({
      key: 'Product',
      legend: 'Global__Product',
      config: [
        new ApDynformsConfigComboBox({
          key: 'Group',
          label: 'Global__Lbl_Group',
          options: [
            // {key: -1, text: 'Croptype_Not_Specified'},
            {key: 0, text: 'Nutrients_Pages_Popups__Inorganic'},
            {key: 1, text: 'Nutrients_Pages_Popups__Organic'},
          ],
          textField: 'text',
          valueField: 'key',
          translate: true,
          valuePrimitive: true,
          value: formValues.Group ?? this._getDefaultGroup(),
          validators: [
            new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'}),
          ]
        }),
        new ApDynformsConfigComboBox({
          key: 'Product',
          label: 'Global__Product',
          valueField: 'Id',
          textField: 'Prodname',
          valuePrimitive: true,
          value: null,
          options: fertilizerOptions,
          value$: combineLatest([
            formValues$,
            this.fertilizerStore.OrganicFertilizer$,
            this.fertilizerStore.InorganicFertilizer$,
            fertilizerOptions
          ]).pipe(
            filter(([values]) => {
              return values['Group'] === formValues.Group || values['Group'] === -1;
            }),
            map(([values]) => {
              return values['Group'] === -1 ? 0 : this._selectableProduct(formValues['Product']);
            }),
            delay(1),
          ),
          validators: [
            new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'}),
          ]
        })
      ]
    });
  }

  private _getDefaultGroup(): number {
    if (this.fertilizerInorganicStore.Listen(s => s.data).getValue().filter((i) => i.Selectable && i.ContentN > 0).length === 0 &&
      this.fertilizerOrganicStore.Listen(s => s.data).getValue().filter((o) => o.Selectable && o.ContentN > 0).length !== 0) {
      return 1;
    }
    return 0;
  }

  private _selectableProduct(productId: number): number {
    const fertilizer = this.fertilizerStore.getFertilizer(productId);
    if (!fertilizer) {
      return null;
    }
    if (fertilizer.Selectable === true) {
      return productId;
    }
    return null;
  }
}
